import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Gift, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import api from "@/services/api";

const DEFAULT_POLICY = {
  rules_and_conditions:
    "1. Bonuses, commissions, and rewards are processed only as per official system rules and eligibility criteria.\n" +
    "2. Fake orders, document forgery, misuse, or any fraudulent activity may result in immediate account suspension or termination.\n" +
    "3. The company may update policies, plans, and operational rules with notice through official channels.",
};

export default function RegisterPage() {
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({ name: "", email: "", phone: "", dob: "", pan_no: "", password: "", sponsor_code: refFromUrl });
  const [loading, setLoading] = useState(false);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [signupBonus, setSignupBonus] = useState(0);
  const [smartCycleBonus, setSmartCycleBonus] = useState(10);
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  // On mount: pull signup bonus + smart cycle % + resolve sponsor code (either from URL or manual entry)
  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setSignupBonus(Number(s.referral_signup_bonus) || 0);
      setSmartCycleBonus(Number(s.smart_cycle_bonus_percent) || 10);
      setPolicy({
        rules_and_conditions: (s.rules_and_conditions || "").trim() || DEFAULT_POLICY.rules_and_conditions,
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const code = (form.sponsor_code || "").trim().toUpperCase();
    if (!code) { setSponsorInfo(null); return; }
    const t = setTimeout(() => {
      api.get(`/auth/sponsor-info/${encodeURIComponent(code)}`)
        .then((r) => setSponsorInfo(r.data))
        .catch(() => setSponsorInfo({ error: true, member_code: code }));
    }, 300);
    return () => clearTimeout(t);
  }, [form.sponsor_code]);

  useEffect(() => {
    if (refFromUrl) {
      try {
        localStorage.setItem("metho_ref_code", refFromUrl);
      } catch {}
      setForm((f) => ({ ...f, sponsor_code: refFromUrl }));
      return;
    }
    try {
      const savedRef = (localStorage.getItem("metho_ref_code") || "").trim().toUpperCase();
      if (savedRef) {
        setForm((f) => ({ ...f, sponsor_code: f.sponsor_code || savedRef }));
      }
    } catch {}
  }, [refFromUrl]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const getErrorMessage = (err) => {
    if (err?.code === "ERR_NETWORK" || !err?.response) {
      return "Server unreachable. Please start backend and try again.";
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === "string" && first.trim()) return first;
      if (first?.msg) return String(first.msg);
    }
    if (detail && typeof detail === "object") {
      if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
      if (typeof detail.msg === "string" && detail.msg.trim()) return detail.msg;
    }
    if (typeof err?.message === "string" && err.message.trim()) return err.message;
    return "Registration failed";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      return toast.error("Please accept Terms & Conditions to continue");
    }
    setLoading(true);
    try {
      // Read directly from form to avoid browser autofill/state desync issues.
      const formData = new FormData(e.currentTarget);
      const payload = {
        name: String(formData.get("name") ?? form.name).trim(),
        email: String(formData.get("email") ?? form.email).trim(),
        phone: String(formData.get("phone") ?? form.phone).trim(),
        dob: String(formData.get("dob") ?? form.dob).trim(),
        pan_no: String(formData.get("pan_no") ?? form.pan_no).trim().toUpperCase(),
        password: String(formData.get("password") ?? form.password),
        sponsor_code: String(formData.get("sponsor_code") ?? form.sponsor_code).trim().toUpperCase(),
      };
      const result = await register(payload);
      if (result?.token && result?.user) {
        toast.success("Registered! Welcome to METHOO STORE.");
        nav("/app");
      } else {
        toast.success("Registration complete. Your member account will activate after first approved METHO purchase.");
        nav("/login");
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8 md:p-16 bg-background order-2 md:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div className="md:hidden"><Logo /></div>
          <div>
            <h2 className="font-display font-black text-3xl text-emerald-950 tracking-tight">Create Account</h2>
            <p className="text-sm text-muted-foreground mt-1 font-body">Join thousands of METHO Partners today</p>
          </div>

          {/* Perk strip — always visible, drives conversion */}
          <div className="flex flex-wrap gap-2" data-testid="register-perks">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-900 px-2.5 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> Free lifetime
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-900 px-2.5 py-1 rounded-full">
              <Gift className="w-3 h-3" /> First order · {smartCycleBonus}% Smart Cycle back
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-900 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> Wallet active Day 1
            </span>
          </div>

          {sponsorInfo && !sponsorInfo.error && (
            <div className="rounded-xl bg-gradient-to-r from-amber-50 to-emerald-50 border border-amber-300 p-4 flex items-start gap-3" data-testid="sponsor-confirm-card">
              <div className="w-10 h-10 rounded-full bg-emerald-900 text-amber-400 flex items-center justify-center font-display font-black shrink-0">
                {(sponsorInfo.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">Referred by</p>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                </div>
                <p className="font-display font-bold text-emerald-950 truncate">{sponsorInfo.name}</p>
                <div className="flex items-center gap-2 mt-0.5 text-xs">
                  <span className="font-mono text-emerald-800 bg-white/70 px-1.5 py-0.5 rounded">{sponsorInfo.member_code}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-emerald-800 font-semibold">{sponsorInfo.rank}</span>
                </div>
                {signupBonus > 0 && (
                  <p className="text-[11px] text-emerald-900 mt-2 font-body">
                    <Gift className="w-3 h-3 inline mr-1 text-amber-600" />
                    Your sponsor earns <span className="font-bold">₹{signupBonus}</span> instant signup bonus when you register.
                  </p>
                )}
              </div>
            </div>
          )}
          {sponsorInfo?.error && form.sponsor_code && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2" data-testid="sponsor-invalid-card">
              <AlertCircle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
              <div className="text-xs text-red-800">
                Sponsor code <span className="font-mono font-bold">{sponsorInfo.member_code}</span> not found. Please double-check with your referrer.
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" name="name" required value={form.name} onChange={setField("name")} placeholder="Md. Rahim Uddin" data-testid="register-name-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Username</Label>
              <Input id="email" name="email" type="text" required value={form.email} onChange={setField("email")} placeholder="Choose your username" data-testid="register-email-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" name="phone" required value={form.phone} onChange={setField("phone")} placeholder="+8801XXXXXXXXX" data-testid="register-phone-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="dob">Date of Birth <span className="text-red-600">*</span></Label>
              <Input id="dob" name="dob" type="date" required value={form.dob} onChange={setField("dob")} data-testid="register-dob-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="pan_no">PAN Number <span className="text-red-600">*</span></Label>
              <Input id="pan_no" name="pan_no" required value={form.pan_no} onChange={setField("pan_no")} placeholder="ABCDE1234F" data-testid="register-pan-input" className="mt-1.5 h-11 uppercase" maxLength={10} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" required minLength={6} value={form.password} onChange={setField("password")} placeholder="Minimum 6 characters" data-testid="register-password-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="sponsor_code" className="flex items-center gap-1.5">
                Sponsor Code
                {refFromUrl ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">
                    <Gift className="w-3 h-3" /> Referred
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                )}
              </Label>
              <Input id="sponsor_code" name="sponsor_code" value={form.sponsor_code} onChange={setField("sponsor_code")} placeholder="MTH-XXXXXX" data-testid="register-sponsor-input" className={"mt-1.5 h-11 " + (refFromUrl ? "bg-amber-50 border-amber-300" : "")} />
            </div>
          </div>

          <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="member-terms-section">
            <h3 className="font-display font-black text-lg text-emerald-950">Terms & Conditions</h3>
            <p className="text-xs text-emerald-900/80 mt-1">Please review and accept before creating your member account.</p>

            <div className="mt-4 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-900 font-semibold">Terms & Conditions</p>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{policy.rules_and_conditions}</p>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="register-terms-checkbox-wrap">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-emerald-400"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  data-testid="register-terms-checkbox"
                />
                <span>I have read and agree to the Terms & Conditions.</span>
              </label>
            </div>
          </section>

          <Button type="submit" disabled={loading || !agreedToTerms} className="w-full h-12 bg-emerald-900 hover:bg-emerald-950 text-white rounded-full font-semibold" data-testid="register-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <ArrowRight className="ml-2 w-4 h-4" /></>}
          </Button>
          <p className="text-center text-sm text-muted-foreground font-body">
            Already registered? <Link to="/login" className="text-emerald-900 font-semibold hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </p>
        </form>
      </div>

      <div className="hidden md:flex bg-gradient-to-br from-emerald-950 to-emerald-800 text-white p-12 flex-col justify-between relative overflow-hidden order-1 md:order-2">
        <div className="absolute inset-0 grain opacity-30" />
        <Logo />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Get Started</p>
          <h1 className="mt-4 font-display font-black text-5xl leading-none tracking-tight">
            Your income<br /><span className="text-amber-400 italic">journey starts here.</span>
          </h1>
          <ul className="mt-6 space-y-3 max-w-sm font-body">
            {["Free lifetime membership", "Instant wallet activation", "Real-time reward distribution from Day 1", "Genealogy tree access"].map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-emerald-100/90 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-emerald-100/50 relative">© 2026 METHOO STORE</p>
      </div>
    </div>
  );
}

